export const ORTHO_SPECIALTIES = [
  'Ortopedista',
  'Traumatologista',
  'Médico',
] as const;

export type OrthoSpecialty = (typeof ORTHO_SPECIALTIES)[number];

export const ORTHO_SUBSPECIALTY_SUGGESTIONS = [
  'Cirurgia da Mão',
  'Coluna',
  'Joelho',
  'Ombro e Cotovelo',
  'Quadril',
  'Ortopedia Pediátrica',
  'Trauma',
  'Medicina Esportiva',
] as const;

export type NormalizedDoctorProfile = {
  fullName: string;
  headerTitlePrimary: string;
  headerTitleSecondary: string;
  bannerTagline: string;
  bannerSupportText: string;
  bannerAccentTone: string;
  bannerTextColor: string;
  bannerPrimaryColor: string;
  bannerAccentColor: string;
  bannerBackgroundStyle: string;
  bannerBackgroundSpec: string;
  bannerLayoutVariant: string;
  bannerLayoutSpec: string;
  bannerPaletteSpec: string;
  bannerTypographySpec: string;
  bannerDecorativeSpec: string;
  headerName: string;
  bannerMode: string;
  bannerTemplateId: BannerTemplateId;
  bannerLayoutPreset: BannerConfig['layoutPreset'];
  bannerOffsetLeftX: number;
  bannerOffsetLeftY: number;
  bannerOffsetRightX: number;
  bannerOffsetRightY: number;
  crm: string;
  crmState: string;
  crmUf: string;
  rqe: string;
  specialty: OrthoSpecialty;
  subspecialty: string;
  hospital: string;
  orthoId: string;
  email: string;
  phone: string;
  website: string;
  logo?: string;
  banner?: string;
  signature?: string;
};

export type BannerTemplateId =
  | 'dark-premium'
  | 'light-clinical'
  | 'abstract-medical'
  | 'tech-orthopedic'
  | 'minimal-luxury'
  | 'modern'
  | 'hospital'
  | 'premium'
  | 'minimal'
  | 'custom';

export type BannerConfig = {
  mode: 'default' | 'ai' | 'upload';
  templateId: BannerTemplateId;
  layoutPreset: 'compact' | 'balanced' | 'spacious';
  layoutVariant: string;
  layoutSpec: string;
  leftOffsetX: number;
  leftOffsetY: number;
  rightOffsetX: number;
  rightOffsetY: number;
  title: string;
  subtitle: string;
  tagline: string;
  supportText: string;
  accentTone: string;
  textColor: string;
  primaryColor: string;
  accentColor: string;
  backgroundStyle: string;
  backgroundSpec: string;
  paletteSpec: string;
  typographySpec: string;
  decorativeSpec: string;
  displayName: string;
  doctorName: string;
  specialty: string;
  hospital: string;
  phone: string;
  email: string;
  site: string;
  crm: string;
  crmState: string;
  rqe: string;
  bannerImageUrl: string;
};

export type BannerLayoutMetrics = {
  leftX: number;
  leftY: number;
  leftWidth: number;
  rightX: number;
  rightY: number;
  rightWidth: number;
  titleFontSize: number;
  subtitleFontSize: number;
  displayNameFontSize: number;
  lineFontSize: number;
  taglineFontSize: number;
  supportTextFontSize: number;
  titleLineHeight: number;
  subtitleGap: number;
  taglineGap: number;
  supportTextGap: number;
  lineGap: number;
};

const cleanText = (value: unknown) =>
  String(value ?? '')
    .replace(/\r/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeLookup = (value: unknown) =>
  cleanText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const clampNumber = (value: unknown, min: number, max: number, fallback: number) => {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(min, Math.min(max, numeric));
};

const normalizeBannerVariant = (value: unknown) => {
  const normalized = cleanText(value).toLowerCase();
  if (normalized.includes('left_hero') || normalized.includes('left-hero')) return 'left_hero';
  if (normalized.includes('right_compact') || normalized.includes('right-compact')) return 'right_compact';
  if (normalized.includes('split-balance')) return 'split_balance';
  if (normalized.includes('centered-title')) return 'centered_title';
  if (normalized.includes('wide-minimal')) return 'wide_minimal';
  if (normalized.includes('institutional-clean')) return 'institutional_clean';
  if (normalized.includes('airy')) return 'airy';
  if (normalized === 'balanced') return 'balanced';
  if (normalized.includes('split')) return 'split';
  return 'balanced';
};

const normalizeBannerSpec = (value: unknown, fallback = '') => {
  const normalized = cleanText(value).toLowerCase().replace(/\s+/g, '-');
  return normalized || fallback;
};

const normalizeBannerLayoutPreset = (value: unknown): BannerConfig['layoutPreset'] => {
  const normalized = cleanText(value).toLowerCase();
  if (normalized === 'compact') return 'compact';
  if (normalized === 'spacious') return 'spacious';
  return 'balanced';
};

export const resolveBannerTonePalette = (tone: unknown) => {
  const normalized = cleanText(tone).toLowerCase();
  if (normalized === 'blue') {
    return { subtitle: '#93C5FD', tagline: 'rgba(226, 232, 240, 0.92)', right: '#FFFFFF' };
  }
  if (normalized === 'teal') {
    return { subtitle: '#5EEAD4', tagline: 'rgba(226, 232, 240, 0.92)', right: '#FFFFFF' };
  }
  if (normalized === 'ice') {
    return { subtitle: '#7DD3FC', tagline: 'rgba(241, 245, 249, 0.92)', right: '#FFFFFF' };
  }
  if (normalized === 'cyan') {
    return { subtitle: '#67E8F9', tagline: 'rgba(226, 232, 240, 0.92)', right: '#FFFFFF' };
  }
  return { subtitle: '#93C5FD', tagline: 'rgba(226, 232, 240, 0.92)', right: '#FFFFFF' };
};

export const resolveBannerTextColor = (value: unknown) => {
  const normalized = cleanText(value).toLowerCase();
  if (!normalized) return '#FFFFFF';
  if (normalized === 'white') return '#FFFFFF';
  if (normalized === 'cyan') return '#67E8F9';
  if (normalized === 'blue') return '#93C5FD';
  if (normalized === 'teal') return '#5EEAD4';
  if (normalized === 'ice') return '#BAE6FD';
  if (/^#([0-9a-f]{3}){1,2}$/i.test(normalized)) return normalized;
  return '#FFFFFF';
};

export const resolveBannerColor = (value: unknown, fallback: string) => {
  const normalized = cleanText(value);
  if (!normalized) return fallback;
  if (/^#([0-9a-f]{3}){1,2}$/i.test(normalized)) return normalized;
  const lower = normalized.toLowerCase();
  if (lower === 'white') return '#FFFFFF';
  if (lower === 'black') return '#000000';
  if (lower === 'cyan') return '#67E8F9';
  if (lower === 'blue') return '#93C5FD';
  if (lower === 'teal') return '#5EEAD4';
  if (lower === 'ice') return '#BAE6FD';
  if (lower === 'navy') return '#1E3A8A';
  return fallback;
};

export const resolveBannerBackdropPalette = (config: {
  backgroundStyle?: unknown;
  backgroundSpec?: unknown;
  primaryColor?: unknown;
  accentColor?: unknown;
}) => {
  const style = cleanText(config.backgroundStyle || config.backgroundSpec || '').toLowerCase();
  const isLightFamily =
    style.includes('light') ||
    style.includes('clean') ||
    style.includes('minimal') ||
    style.includes('hospital') ||
    style.includes('pearl') ||
    style.includes('luxury');
  const isTechFamily = style.includes('tech') || style.includes('orthopedic') || style.includes('data');
  const primaryFallback =
    isLightFamily
      ? '#F8FAFC'
      : style.includes('abstract') || style.includes('texture') || style.includes('geometric')
        ? '#10253F'
      : isTechFamily
          ? '#0F172A'
          : '#0B1320';
  const accentFallback =
    isLightFamily
      ? '#7DD3FC'
      : style.includes('abstract') || style.includes('texture') || style.includes('geometric')
        ? '#2DD4BF'
      : isTechFamily
          ? '#67E8F9'
          : '#22D3EE';

  const primary = resolveBannerColor(config.primaryColor, primaryFallback);
  const accent = resolveBannerColor(config.accentColor, accentFallback);

  if (isLightFamily) {
    return {
      background: 'linear-gradient(135deg, rgba(248,250,252,0.95) 0%, rgba(239,246,255,0.92) 50%, rgba(219,234,254,0.88) 100%)',
      glow: `radial-gradient(circle at 14% 32%, ${accent}33 0%, transparent 34%), radial-gradient(circle at 72% 28%, ${primary}18 0%, transparent 28%)`,
      grid: 'linear-gradient(rgba(148,163,184,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.12) 1px, transparent 1px)',
      border: 'rgba(148,163,184,0.18)',
    };
  }

  if (style.includes('abstract') || style.includes('texture') || style.includes('geometric')) {
    return {
      background: `linear-gradient(135deg, ${primary}F2 0%, #0F1D33 55%, #0B1324 100%)`,
      glow: `radial-gradient(circle at 12% 28%, ${accent}26 0%, transparent 32%), radial-gradient(circle at 84% 22%, ${accent}1A 0%, transparent 24%)`,
      grid: 'linear-gradient(rgba(148,163,184,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.10) 1px, transparent 1px)',
      border: 'rgba(96,165,250,0.18)',
    };
  }

  if (isTechFamily) {
    return {
      background: `linear-gradient(145deg, ${primary}F2 0%, #13213A 48%, #0A1324 100%)`,
      glow: `radial-gradient(circle at 18% 26%, ${accent}2B 0%, transparent 30%), radial-gradient(circle at 78% 34%, ${accent}20 0%, transparent 24%)`,
      grid: 'radial-gradient(circle at center, rgba(125,211,252,0.10) 0, rgba(125,211,252,0.10) 1px, transparent 1px, transparent 16px)',
      border: 'rgba(125,211,252,0.20)',
    };
  }

  if (style.includes('dark') || style.includes('premium')) {
    return {
      background: `linear-gradient(135deg, ${primary}F2 0%, #102A43 48%, ${accent}22 100%)`,
      glow: `radial-gradient(circle at 18% 30%, ${accent}30 0%, transparent 32%), radial-gradient(circle at 82% 24%, ${accent}1D 0%, transparent 26%)`,
      grid: 'linear-gradient(rgba(56,189,248,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.10) 1px, transparent 1px)',
      border: 'rgba(56,189,248,0.20)',
    };
  }

  if (style.includes('minimal') || style.includes('luxury') || style.includes('institutional')) {
    return {
      background: 'linear-gradient(135deg, rgba(248,250,252,0.98) 0%, rgba(236,249,255,0.94) 58%, rgba(219,234,254,0.88) 100%)',
      glow: `radial-gradient(circle at 16% 26%, ${accent}22 0%, transparent 30%), radial-gradient(circle at 80% 30%, ${primary}12 0%, transparent 26%)`,
      grid: 'linear-gradient(rgba(15,23,42,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.08) 1px, transparent 1px)',
      border: 'rgba(148,163,184,0.16)',
    };
  }

  return {
    background: `linear-gradient(135deg, ${primary}F2 0%, #0B1220 60%, ${accent}18 100%)`,
    glow: `radial-gradient(circle at 16% 28%, ${accent}28 0%, transparent 32%), radial-gradient(circle at 76% 26%, ${accent}1C 0%, transparent 26%)`,
    grid: 'linear-gradient(rgba(96,165,250,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.08) 1px, transparent 1px)',
    border: 'rgba(96,165,250,0.18)',
  };
};

export const resolveOrthoBannerTemplateAsset = (templateId: unknown) => {
  const normalized = cleanText(templateId).toLowerCase();
  if (normalized === 'tech-orthopedic' || normalized === 'modern') return '/ortho-banner-template-tech-orthopedic.svg';
  if (normalized === 'light-clinical' || normalized === 'hospital') return '/ortho-banner-template-light-clinical.svg';
  if (normalized === 'dark-premium' || normalized === 'premium') return '/ortho-banner-template-dark-premium.svg';
  if (normalized === 'minimal-luxury' || normalized === 'minimal') return '/ortho-banner-template-minimal-luxury.svg';
  if (normalized === 'abstract-medical' || normalized === 'custom') return '/ortho-banner-template-abstract-medical.svg';
  return '/ortho-banner-bg.svg';
};

export const normalizeOrthoSpecialty = (value: unknown): OrthoSpecialty => {
  const normalized = normalizeLookup(value);

  if (
    normalized.includes('traumatolog') ||
    normalized.includes('trauma') ||
    normalized === 'traumato'
  ) {
    return 'Traumatologista';
  }

  if (
    normalized.includes('ortoped') ||
    normalized.includes('orthoped') ||
    normalized.includes('cirurgiao ortopedista') ||
    normalized.includes('cirurgiao ortopedico')
  ) {
    return 'Ortopedista';
  }

  if (
    normalized.includes('medico') ||
    normalized.includes('physician') ||
    normalized.includes('doctor') ||
    normalized.includes('clinica geral')
  ) {
    return 'Médico';
  }

  return ORTHO_SPECIALTIES.includes(cleanText(value) as OrthoSpecialty)
    ? (cleanText(value) as OrthoSpecialty)
    : 'Médico';
};

export const normalizeDoctorProfile = (...sources: Array<any>): NormalizedDoctorProfile => {
  const pickText = (...values: Array<string | number | null | undefined>) =>
    values.map((value) => cleanText(value)).find((value) => Boolean(value)) || '';

  const pickFromSources = (paths: Array<(source: any) => unknown>) => {
    for (const source of sources) {
      for (const getValue of paths) {
        const value = getValue(source);
        const text = cleanText(value);
        if (text) return text;
      }
    }
    return '';
  };

  const fullName = pickFromSources([
    (s) => s?.profile?.name,
    (s) => s?.profile?.fullName,
    (s) => s?.profile?.displayName,
    (s) => s?.profile?.doctorName,
    (s) => s?.professionalVerification?.officialName,
    (s) => s?.professionalVerification?.name,
    (s) => s?.name,
    (s) => s?.fullName,
    (s) => s?.displayName,
    (s) => s?.doctorName,
  ]);

  const crm = pickFromSources([
    (s) => s?.profile?.crm,
    (s) => s?.profile?.crmNumber,
    (s) => s?.profile?.crmRegistration,
    (s) => s?.professionalVerification?.crm,
    (s) => s?.professionalVerification?.crmNumber,
    (s) => s?.professionalVerification?.crmRegistration,
    (s) => s?.crm,
    (s) => s?.crmNumber,
    (s) => s?.crmRegistration,
  ]);

  const crmState = pickFromSources([
    (s) => s?.profile?.crmState,
    (s) => s?.profile?.crmUf,
    (s) => s?.profile?.uf,
    (s) => s?.professionalVerification?.crmState,
    (s) => s?.professionalVerification?.crmUf,
    (s) => s?.professionalVerification?.uf,
    (s) => s?.crmState,
    (s) => s?.crmUf,
    (s) => s?.uf,
    (s) => s?.state,
  ]).toUpperCase();

  const specialty = normalizeOrthoSpecialty(
    pickFromSources([
      (s) => s?.profile?.specialty,
      (s) => s?.profile?.speciality,
      (s) => s?.profile?.specialization,
      (s) => s?.professionalVerification?.specialty,
      (s) => s?.professionalVerification?.speciality,
      (s) => s?.specialty,
      (s) => s?.especialidade,
      (s) => s?.doctorSpecialty,
    ])
  );

  const subspecialty = pickFromSources([
    (s) => s?.profile?.subspecialty,
    (s) => s?.profile?.subEspecialidade,
    (s) => s?.profile?.subspeciality,
    (s) => s?.profile?.subspecialization,
    (s) => s?.professionalVerification?.subspecialty,
    (s) => s?.subspecialty,
    (s) => s?.subEspecialidade,
    (s) => s?.subspeciality,
    (s) => s?.subspecialization,
  ]);

  const hospital = pickFromSources([
    (s) => s?.profile?.hospital,
    (s) => s?.profile?.hospitalName,
    (s) => s?.profile?.clinic,
    (s) => s?.profile?.clinicName,
    (s) => s?.profile?.workplace,
    (s) => s?.profile?.institution,
    (s) => s?.hospital,
    (s) => s?.hospitalName,
    (s) => s?.clinic,
    (s) => s?.clinicName,
    (s) => s?.workplace,
    (s) => s?.institution,
  ]);

  const orthoId = pickFromSources([
    (s) => s?.profile?.orthoId,
    (s) => s?.profile?.username,
    (s) => s?.profile?.email,
    (s) => s?.orthoId,
    (s) => s?.username,
    (s) => s?.email,
    (s) => s?.uid,
  ]);

  const email = pickFromSources([
    (s) => s?.profile?.email,
    (s) => s?.email,
  ]);

  const phone = pickFromSources([
    (s) => s?.profile?.phone,
    (s) => s?.phone,
  ]);

  const headerName = pickFromSources([
    (s) => s?.profile?.headerName,
    (s) => s?.profile?.brandingHeaderName,
    (s) => s?.profile?.displayName,
    (s) => s?.headerName,
    (s) => s?.brandingHeaderName,
    (s) => s?.displayName,
    (s) => s?.name,
    (s) => s?.fullName,
  ]);

  const headerTitlePrimary = pickFromSources([
    (s) => s?.profile?.headerTitlePrimary,
    (s) => s?.profile?.brandingHeaderTitlePrimary,
    (s) => s?.headerTitlePrimary,
    (s) => s?.brandingHeaderTitlePrimary,
  ]);

  const headerTitleSecondary = pickFromSources([
    (s) => s?.profile?.headerTitleSecondary,
    (s) => s?.profile?.brandingHeaderTitleSecondary,
    (s) => s?.headerTitleSecondary,
    (s) => s?.brandingHeaderTitleSecondary,
  ]);

  const website = pickFromSources([
    (s) => s?.profile?.website,
    (s) => s?.profile?.site,
    (s) => s?.profile?.websiteUrl,
    (s) => s?.website,
    (s) => s?.site,
    (s) => s?.websiteUrl,
  ]);

  const logo = pickFromSources([
    (s) => s?.profile?.logo,
    (s) => s?.profile?.logoUrl,
    (s) => s?.profile?.logoImage,
    (s) => s?.profile?.brandLogo,
    (s) => s?.profile?.brandingLogo,
    (s) => s?.profile?.hospitalLogo,
    (s) => s?.logo,
    (s) => s?.logoUrl,
    (s) => s?.logoImage,
    (s) => s?.brandLogo,
    (s) => s?.brandingLogo,
    (s) => s?.hospitalLogo,
    (s) => s?.branding?.logo,
    (s) => s?.hospital?.logo,
  ]);

  const banner = pickFromSources([
    (s) => s?.profile?.banner,
    (s) => s?.profile?.bannerUrl,
    (s) => s?.profile?.bannerImage,
    (s) => s?.profile?.brandingBanner,
    (s) => s?.profile?.headerBanner,
    (s) => s?.banner,
    (s) => s?.bannerUrl,
    (s) => s?.bannerImage,
    (s) => s?.brandingBanner,
    (s) => s?.headerBanner,
    (s) => s?.branding?.banner,
  ]);

  const bannerMode = pickFromSources([
    (s) => s?.profile?.bannerMode,
    (s) => s?.profile?.brandingBannerMode,
    (s) => s?.bannerMode,
    (s) => s?.brandingBannerMode,
  ]).toLowerCase();

  const bannerTagline = pickFromSources([
    (s) => s?.profile?.bannerTagline,
    (s) => s?.profile?.brandingBannerTagline,
    (s) => s?.bannerTagline,
    (s) => s?.brandingBannerTagline,
    (s) => s?.headerTagline,
  ]);
  const bannerSupportText = pickFromSources([
    (s) => s?.profile?.bannerSupportText,
    (s) => s?.profile?.brandingBannerSupportText,
    (s) => s?.bannerSupportText,
    (s) => s?.brandingBannerSupportText,
    (s) => s?.headerSupportText,
  ]);
  const bannerAccentTone = pickFromSources([
    (s) => s?.profile?.bannerAccentTone,
    (s) => s?.profile?.brandingBannerAccentTone,
    (s) => s?.bannerAccentTone,
    (s) => s?.brandingBannerAccentTone,
    (s) => s?.accentTone,
  ]);
  const bannerTextColor = pickFromSources([
    (s) => s?.profile?.bannerTextColor,
    (s) => s?.profile?.brandingBannerTextColor,
    (s) => s?.bannerTextColor,
    (s) => s?.brandingBannerTextColor,
    (s) => s?.headerTextColor,
  ]);
  const bannerPrimaryColor = pickFromSources([
    (s) => s?.profile?.bannerPrimaryColor,
    (s) => s?.profile?.brandingBannerPrimaryColor,
    (s) => s?.bannerPrimaryColor,
    (s) => s?.brandingBannerPrimaryColor,
    (s) => s?.headerPrimaryColor,
  ]);
  const bannerAccentColor = pickFromSources([
    (s) => s?.profile?.bannerAccentColor,
    (s) => s?.profile?.brandingBannerAccentColor,
    (s) => s?.bannerAccentColor,
    (s) => s?.brandingBannerAccentColor,
    (s) => s?.headerAccentColor,
  ]);
  const bannerBackgroundStyle = pickFromSources([
    (s) => s?.profile?.bannerBackgroundStyle,
    (s) => s?.profile?.brandingBannerBackgroundStyle,
    (s) => s?.bannerBackgroundStyle,
    (s) => s?.brandingBannerBackgroundStyle,
    (s) => s?.headerBackgroundStyle,
  ]);
  const bannerBackgroundSpec = pickFromSources([
    (s) => s?.profile?.bannerBackgroundSpec,
    (s) => s?.profile?.brandingBannerBackgroundSpec,
    (s) => s?.bannerBackgroundSpec,
    (s) => s?.brandingBannerBackgroundSpec,
    (s) => s?.headerBackgroundSpec,
  ]);
  const bannerLayoutVariant = normalizeBannerVariant(
    pickFromSources([
      (s) => s?.profile?.bannerLayoutVariant,
      (s) => s?.profile?.brandingBannerLayoutVariant,
      (s) => s?.bannerLayoutVariant,
      (s) => s?.brandingBannerLayoutVariant,
      (s) => s?.headerLayoutVariant,
    ])
  );
  const bannerLayoutSpec = normalizeBannerSpec(
    pickFromSources([
      (s) => s?.profile?.bannerLayoutSpec,
      (s) => s?.profile?.brandingBannerLayoutSpec,
      (s) => s?.bannerLayoutSpec,
      (s) => s?.brandingBannerLayoutSpec,
      (s) => s?.headerLayoutSpec,
    ]),
    bannerLayoutVariant
  );
  const bannerPaletteSpec = normalizeBannerSpec(
    pickFromSources([
      (s) => s?.profile?.bannerPaletteSpec,
      (s) => s?.profile?.brandingBannerPaletteSpec,
      (s) => s?.bannerPaletteSpec,
      (s) => s?.brandingBannerPaletteSpec,
      (s) => s?.headerPaletteSpec,
    ])
  );
  const bannerTypographySpec = normalizeBannerSpec(
    pickFromSources([
      (s) => s?.profile?.bannerTypographySpec,
      (s) => s?.profile?.brandingBannerTypographySpec,
      (s) => s?.bannerTypographySpec,
      (s) => s?.brandingBannerTypographySpec,
      (s) => s?.headerTypographySpec,
    ])
  );
  const bannerDecorativeSpec = normalizeBannerSpec(
    pickFromSources([
      (s) => s?.profile?.bannerDecorativeSpec,
      (s) => s?.profile?.brandingBannerDecorativeSpec,
      (s) => s?.bannerDecorativeSpec,
      (s) => s?.brandingBannerDecorativeSpec,
      (s) => s?.headerDecorativeSpec,
    ])
  );

  const bannerTemplateId = (() => {
    const normalized = cleanText(
      pickFromSources([
        (s) => s?.profile?.bannerTemplateId,
        (s) => s?.profile?.brandingBannerTemplateId,
        (s) => s?.bannerTemplateId,
        (s) => s?.brandingBannerTemplateId,
        (s) => s?.headerTemplateId,
      ])
    ).toLowerCase();
    if (
      normalized === 'dark-premium' ||
      normalized === 'light-clinical' ||
      normalized === 'abstract-medical' ||
      normalized === 'tech-orthopedic' ||
      normalized === 'minimal-luxury' ||
      normalized === 'modern' ||
      normalized === 'hospital' ||
      normalized === 'minimal' ||
      normalized === 'premium' ||
      normalized === 'custom'
    ) {
      return normalized as BannerTemplateId;
    }
    return 'dark-premium';
  })();

  const bannerLayoutPreset = normalizeBannerLayoutPreset(
    pickFromSources([
      (s) => s?.profile?.bannerLayoutPreset,
      (s) => s?.profile?.brandingBannerLayoutPreset,
      (s) => s?.bannerLayoutPreset,
      (s) => s?.brandingBannerLayoutPreset,
      (s) => s?.headerLayoutPreset,
    ])
  );
  const bannerOffsetLeftX = clampNumber(
    pickFromSources([
      (s) => s?.profile?.bannerOffsetLeftX,
      (s) => s?.profile?.brandingBannerOffsetLeftX,
      (s) => s?.bannerOffsetLeftX,
      (s) => s?.brandingBannerOffsetLeftX,
      (s) => s?.headerOffsetLeftX,
    ]),
    -12,
    12,
    0
  );
  const bannerOffsetLeftY = clampNumber(
    pickFromSources([
      (s) => s?.profile?.bannerOffsetLeftY,
      (s) => s?.profile?.brandingBannerOffsetLeftY,
      (s) => s?.bannerOffsetLeftY,
      (s) => s?.brandingBannerOffsetLeftY,
      (s) => s?.headerOffsetLeftY,
    ]),
    -12,
    12,
    0
  );
  const bannerOffsetRightX = clampNumber(
    pickFromSources([
      (s) => s?.profile?.bannerOffsetRightX,
      (s) => s?.profile?.brandingBannerOffsetRightX,
      (s) => s?.bannerOffsetRightX,
      (s) => s?.brandingBannerOffsetRightX,
      (s) => s?.headerOffsetRightX,
    ]),
    -12,
    12,
    0
  );
  const bannerOffsetRightY = clampNumber(
    pickFromSources([
      (s) => s?.profile?.bannerOffsetRightY,
      (s) => s?.profile?.brandingBannerOffsetRightY,
      (s) => s?.bannerOffsetRightY,
      (s) => s?.brandingBannerOffsetRightY,
      (s) => s?.headerOffsetRightY,
    ]),
    -12,
    12,
    0
  );

  const signature = pickFromSources([
    (s) => s?.profile?.signature?.url,
    (s) => s?.profile?.signature?.dataUrl,
    (s) => s?.profile?.signature?.image,
    (s) => s?.profile?.signatureUrl,
    (s) => s?.profile?.signatureImage,
    (s) => s?.profile?.signatureData,
    (s) => s?.profile?.signature,
    (s) => s?.signature?.url,
    (s) => s?.signature?.dataUrl,
    (s) => s?.signature?.image,
    (s) => s?.signatureUrl,
    (s) => s?.signatureImage,
    (s) => s?.signatureData,
    (s) => s?.signature,
  ]);

  return {
    fullName,
    headerTitlePrimary,
    headerTitleSecondary,
    bannerTagline,
    bannerSupportText,
    bannerAccentTone,
    bannerTextColor,
    bannerPrimaryColor,
    bannerAccentColor,
    bannerBackgroundStyle,
    bannerLayoutVariant,
    bannerBackgroundSpec,
    bannerLayoutSpec,
    bannerPaletteSpec,
    bannerTypographySpec,
    bannerDecorativeSpec,
    crm,
    crmState,
    crmUf: [crm ? `CRM ${crm}` : '', crmState ? `/${crmState}` : '']
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim(),
    rqe: pickFromSources([
      (s) => s?.profile?.rqe,
      (s) => s?.profile?.rqeNumber,
      (s) => s?.professionalVerification?.rqe,
      (s) => s?.rqe,
      (s) => s?.rqeNumber,
    ]),
    specialty,
    subspecialty,
    hospital,
    orthoId,
    email,
    phone,
    headerName: headerName || fullName,
    bannerMode: bannerMode === 'upload' ? 'upload' : 'ai',
    bannerTemplateId,
    bannerLayoutPreset,
    bannerOffsetLeftX,
    bannerOffsetLeftY,
    bannerOffsetRightX,
    bannerOffsetRightY,
    website,
    logo: logo || undefined,
    banner: banner || undefined,
    signature: signature || undefined,
  };
};

export const formatDoctorSpecialtySummary = (
  doctor: Partial<NormalizedDoctorProfile> | null | undefined
) => {
  const specialty = normalizeOrthoSpecialty(doctor?.specialty || 'Médico');
  const subspecialty = cleanText(doctor?.subspecialty || '');

  return subspecialty ? `${specialty} — ${subspecialty}` : specialty;
};

export const buildDoctorProfilePayload = (doctor: Partial<NormalizedDoctorProfile>) => ({
  fullName: cleanText(doctor.fullName || ''),
  headerTitlePrimary: cleanText(doctor.headerTitlePrimary || ''),
  headerTitleSecondary: cleanText(doctor.headerTitleSecondary || ''),
  bannerTagline: cleanText((doctor as any).bannerTagline || ''),
  bannerSupportText: cleanText((doctor as any).bannerSupportText || ''),
  bannerAccentTone: cleanText((doctor as any).bannerAccentTone || ''),
  bannerTextColor: cleanText((doctor as any).bannerTextColor || ''),
  bannerPrimaryColor: cleanText((doctor as any).bannerPrimaryColor || ''),
  bannerAccentColor: cleanText((doctor as any).bannerAccentColor || ''),
  bannerBackgroundStyle: cleanText((doctor as any).bannerBackgroundStyle || ''),
  bannerLayoutVariant: cleanText((doctor as any).bannerLayoutVariant || ''),
  bannerBackgroundSpec: cleanText((doctor as any).bannerBackgroundSpec || ''),
  bannerLayoutSpec: cleanText((doctor as any).bannerLayoutSpec || ''),
  bannerPaletteSpec: cleanText((doctor as any).bannerPaletteSpec || ''),
  bannerTypographySpec: cleanText((doctor as any).bannerTypographySpec || ''),
  bannerDecorativeSpec: cleanText((doctor as any).bannerDecorativeSpec || ''),
  headerName: cleanText(doctor.headerName || ''),
  bannerMode: cleanText(doctor.bannerMode || 'ai').toLowerCase() === 'upload' ? 'upload' : 'ai',
  bannerTemplateId: (() => {
    const normalized = cleanText((doctor as any).bannerTemplateId || 'premium').toLowerCase();
    if (
      normalized === 'dark-premium' ||
      normalized === 'light-clinical' ||
      normalized === 'abstract-medical' ||
      normalized === 'tech-orthopedic' ||
      normalized === 'minimal-luxury' ||
      normalized === 'modern' ||
      normalized === 'hospital' ||
      normalized === 'minimal' ||
      normalized === 'premium' ||
      normalized === 'custom'
    ) {
      return normalized as BannerTemplateId;
    }
    return 'dark-premium';
  })(),
  bannerLayoutPreset: normalizeBannerLayoutPreset(doctor.bannerLayoutPreset),
  bannerOffsetLeftX: clampNumber(doctor.bannerOffsetLeftX, -12, 12, 0),
  bannerOffsetLeftY: clampNumber(doctor.bannerOffsetLeftY, -12, 12, 0),
  bannerOffsetRightX: clampNumber(doctor.bannerOffsetRightX, -12, 12, 0),
  bannerOffsetRightY: clampNumber(doctor.bannerOffsetRightY, -12, 12, 0),
  crm: cleanText(doctor.crm || ''),
  crmState: cleanText(doctor.crmState || '').toUpperCase(),
  crmUf: cleanText(doctor.crmUf || ''),
  rqe: cleanText(doctor.rqe || ''),
  specialty: normalizeOrthoSpecialty(doctor.specialty || 'Médico'),
  subspecialty: cleanText(doctor.subspecialty || ''),
  hospital: cleanText(doctor.hospital || ''),
  orthoId: cleanText(doctor.orthoId || ''),
  email: cleanText(doctor.email || ''),
  phone: cleanText(doctor.phone || ''),
  website: cleanText(doctor.website || ''),
  logo: cleanText((doctor as any).logo || ''),
  banner: cleanText((doctor as any).banner || ''),
  signature: cleanText(doctor.signature || ''),
});

export const buildBannerConfig = (
  source: Record<string, any> = {},
  fallbackBannerImageUrl = '/ortho-banner-bg.svg'
): BannerConfig => {
  const modeSource = cleanText(source.mode || source.bannerMode || source.brandingBannerMode || '');
  const normalizedMode = modeSource.toLowerCase();
  const mode: BannerConfig['mode'] =
    normalizedMode === 'upload' ? 'upload' : normalizedMode === 'ai' ? 'ai' : 'default';
  const templateId = (() => {
    const normalized = cleanText(
      source.templateId ||
        source.bannerTemplateId ||
        source.brandingBannerTemplateId ||
        source.headerTemplateId
    ).toLowerCase();
    if (
      normalized === 'dark-premium' ||
      normalized === 'light-clinical' ||
      normalized === 'abstract-medical' ||
      normalized === 'tech-orthopedic' ||
      normalized === 'minimal-luxury' ||
      normalized === 'modern' ||
      normalized === 'hospital' ||
      normalized === 'minimal' ||
      normalized === 'premium' ||
      normalized === 'custom'
    ) {
      return normalized as BannerTemplateId;
    }
    return mode === 'ai' ? 'dark-premium' : 'custom';
  })();

  const title = cleanText(
    source.headerTitlePrimary ||
      source.brandingHeaderTitlePrimary ||
      'ORTHO PRECISION'
  ) || 'ORTHO PRECISION';

  const subtitle = cleanText(
    source.headerTitleSecondary ||
      source.brandingHeaderTitleSecondary ||
      'MEDICAL CREDENTIAL'
  ) || 'MEDICAL CREDENTIAL';

  const doctorName = cleanText(source.fullName || source.name || source.doctorName || '');
  const displayName = cleanText(
    source.headerName ||
      source.brandingHeaderName ||
      source.displayName ||
      doctorName
  );

  const specialty = cleanText(source.specialty || source.subspecialty || '');
  const tagline = cleanText(
    source.bannerTagline ||
      source.brandingBannerTagline ||
      source.headerTagline ||
      source.tagline ||
      ''
  );
  const supportText = cleanText(
    source.bannerSupportText ||
      source.brandingBannerSupportText ||
      source.headerSupportText ||
      source.supportText ||
      ''
  );
  const backgroundStyle = cleanText(
    source.backgroundStyle ||
      source.bannerBackgroundStyle ||
      source.brandingBannerBackgroundStyle ||
      source.headerBackgroundStyle ||
      source.backgroundSpec ||
      ''
  ).toLowerCase();
  const backgroundSpec = normalizeBannerSpec(
    source.backgroundSpec ||
      source.bannerBackgroundSpec ||
      source.brandingBannerBackgroundSpec ||
      source.headerBackgroundSpec ||
      backgroundStyle,
    backgroundStyle
  );
  const hospital = cleanText(source.hospital || source.hospitalName || source.clinic || '');
  const phone = cleanText(source.phone || '');
  const email = cleanText(source.email || '');
  const site = cleanText(source.website || source.site || source.websiteUrl || '');
  const crm = cleanText(source.crm || '');
  const crmState = cleanText(source.crmState || source.crmUf || source.uf || '').toUpperCase();
  const rqe = cleanText(source.rqe || '');
  const bannerImageUrl = cleanText(source.bannerImageUrl || source.banner || source.bannerUrl || '');
  const layoutPreset = normalizeBannerLayoutPreset(
    source.bannerLayoutPreset ||
      source.brandingBannerLayoutPreset ||
      source.headerLayoutPreset ||
      source.layoutPreset
  );
  const layoutVariant = normalizeBannerVariant(
    source.bannerLayoutVariant ||
      source.brandingBannerLayoutVariant ||
      source.headerLayoutVariant ||
      source.layoutSpec ||
      source.layoutVariant
  );
  const layoutSpec = normalizeBannerSpec(
    source.bannerLayoutSpec ||
      source.brandingBannerLayoutSpec ||
      source.headerLayoutSpec ||
      source.layoutSpec ||
      layoutVariant,
    layoutVariant
  );
  const paletteSpec = normalizeBannerSpec(
    source.bannerPaletteSpec ||
      source.brandingBannerPaletteSpec ||
      source.headerPaletteSpec ||
      source.paletteSpec ||
      '',
    ''
  );
  const typographySpec = normalizeBannerSpec(
    source.bannerTypographySpec ||
      source.brandingBannerTypographySpec ||
      source.headerTypographySpec ||
      source.typographySpec ||
      '',
    ''
  );
  const decorativeSpec = normalizeBannerSpec(
    source.bannerDecorativeSpec ||
      source.brandingBannerDecorativeSpec ||
      source.headerDecorativeSpec ||
      source.decorativeSpec ||
      '',
    ''
  );
  const leftOffsetX = clampNumber(
    source.bannerOffsetLeftX ?? source.brandingBannerOffsetLeftX ?? source.headerOffsetLeftX,
    -12,
    12,
    0
  );
  const leftOffsetY = clampNumber(
    source.bannerOffsetLeftY ?? source.brandingBannerOffsetLeftY ?? source.headerOffsetLeftY,
    -12,
    12,
    0
  );
  const rightOffsetX = clampNumber(
    source.bannerOffsetRightX ?? source.brandingBannerOffsetRightX ?? source.headerOffsetRightX,
    -12,
    12,
    0
  );
  const rightOffsetY = clampNumber(
    source.bannerOffsetRightY ?? source.brandingBannerOffsetRightY ?? source.headerOffsetRightY,
    -12,
    12,
    0
  );

  return {
    mode,
    templateId,
    layoutPreset,
    layoutVariant,
    leftOffsetX,
    leftOffsetY,
    rightOffsetX,
    rightOffsetY,
    title,
    subtitle,
    tagline,
    supportText,
    accentTone: cleanText(source.accentTone || source.bannerAccentTone || source.brandingBannerAccentTone || ''),
    textColor: cleanText(source.textColor || source.bannerTextColor || source.brandingBannerTextColor || '') || (backgroundStyle === 'light-clinical' ? '#0F172A' : '#FFFFFF'),
    primaryColor: cleanText(source.primaryColor || source.bannerPrimaryColor || source.brandingBannerPrimaryColor || ''),
    accentColor: cleanText(source.accentColor || source.bannerAccentColor || source.brandingBannerAccentColor || ''),
    backgroundStyle,
    backgroundSpec,
    layoutSpec,
    paletteSpec,
    typographySpec,
    decorativeSpec,
    displayName,
    doctorName,
    specialty,
    hospital,
    phone,
    email,
    site,
    crm,
    crmState,
    rqe,
    bannerImageUrl: bannerImageUrl || resolveOrthoBannerTemplateAsset(templateId) || fallbackBannerImageUrl,
  };
};

export const getBannerLayoutMetrics = (
  config: BannerConfig,
  width: number,
  height: number
): BannerLayoutMetrics => {
  const presetKey = config.layoutPreset || 'balanced';
  const variant = normalizeBannerVariant(config.layoutSpec || config.layoutVariant);
  const typography = cleanText((config as any).typographySpec || '').toLowerCase();
  const typographyScale =
    typography.includes('condensed') ? 0.94 :
    typography.includes('technical') ? 0.97 :
    typography.includes('premium') ? 1.03 :
    typography.includes('elegant') ? 1.02 :
    typography.includes('clean') ? 1.0 :
    1.0;
  const presets: Record<BannerConfig['layoutPreset'], Omit<BannerLayoutMetrics, 'leftX' | 'leftY' | 'leftWidth' | 'rightX' | 'rightY' | 'rightWidth'>> = {
    compact: {
      titleFontSize: Math.round(height * 0.145 * typographyScale),
      subtitleFontSize: Math.round(height * 0.052 * typographyScale),
      displayNameFontSize: Math.round(height * 0.078 * typographyScale),
      lineFontSize: Math.round(height * 0.053 * typographyScale),
      taglineFontSize: Math.round(height * 0.038 * typographyScale),
      supportTextFontSize: Math.round(height * 0.032 * typographyScale),
      titleLineHeight: Math.round(height * 0.086 * typographyScale),
      subtitleGap: Math.round(height * 0.05 * typographyScale),
      taglineGap: Math.round(height * 0.046 * typographyScale),
      supportTextGap: Math.round(height * 0.032 * typographyScale),
      lineGap: Math.round(height * 0.056 * typographyScale),
    },
    balanced: {
      titleFontSize: Math.round(height * 0.168 * typographyScale),
      subtitleFontSize: Math.round(height * 0.068 * typographyScale),
      displayNameFontSize: Math.round(height * 0.126 * typographyScale),
      lineFontSize: Math.round(height * 0.074 * typographyScale),
      taglineFontSize: Math.round(height * 0.04 * typographyScale),
      supportTextFontSize: Math.round(height * 0.034 * typographyScale),
      titleLineHeight: Math.round(height * 0.094 * typographyScale),
      subtitleGap: Math.round(height * 0.046 * typographyScale),
      taglineGap: Math.round(height * 0.05 * typographyScale),
      supportTextGap: Math.round(height * 0.036 * typographyScale),
      lineGap: Math.round(height * 0.061 * typographyScale),
    },
    spacious: {
      titleFontSize: Math.round(height * 0.182 * typographyScale),
      subtitleFontSize: Math.round(height * 0.072 * typographyScale),
      displayNameFontSize: Math.round(height * 0.136 * typographyScale),
      lineFontSize: Math.round(height * 0.08 * typographyScale),
      taglineFontSize: Math.round(height * 0.042 * typographyScale),
      supportTextFontSize: Math.round(height * 0.036 * typographyScale),
      titleLineHeight: Math.round(height * 0.1 * typographyScale),
      subtitleGap: Math.round(height * 0.05 * typographyScale),
      taglineGap: Math.round(height * 0.056 * typographyScale),
      supportTextGap: Math.round(height * 0.04 * typographyScale),
      lineGap: Math.round(height * 0.066 * typographyScale),
    },
  };

  const preset = presets[presetKey];
  const leftBaseX = width * (presetKey === 'compact' ? 0.046 : presetKey === 'spacious' ? 0.052 : 0.05);
  const rightBaseX = width * (presetKey === 'compact' ? 0.694 : presetKey === 'spacious' ? 0.704 : 0.7);
  const leftBaseY = height * (presetKey === 'compact' ? 0.155 : presetKey === 'spacious' ? 0.185 : 0.17);
  const rightBaseY = height * (presetKey === 'compact' ? 0.155 : presetKey === 'spacious' ? 0.185 : 0.17);

  const variantAdjustments = {
    left_hero: { leftY: -0.02, rightY: -0.005, leftWidth: 0.02, rightWidth: -0.015 },
    balanced: { leftY: 0, rightY: 0, leftWidth: 0, rightWidth: 0 },
    right_compact: { leftY: 0.005, rightY: 0.01, leftWidth: -0.015, rightWidth: 0.02 },
    airy: { leftY: -0.01, rightY: -0.01, leftWidth: 0.01, rightWidth: 0.01 },
    split: { leftY: 0.005, rightY: 0.005, leftWidth: 0.005, rightWidth: 0.005 },
    split_balance: { leftY: 0.002, rightY: 0.002, leftWidth: 0.01, rightWidth: 0.01 },
    centered_title: { leftY: -0.018, rightY: 0, leftWidth: 0.02, rightWidth: -0.01 },
    wide_minimal: { leftY: -0.006, rightY: -0.006, leftWidth: 0.03, rightWidth: 0.005 },
    institutional_clean: { leftY: -0.01, rightY: -0.008, leftWidth: 0.015, rightWidth: 0.015 },
  }[variant];

  return {
    leftX: Math.round(leftBaseX + config.leftOffsetX * (width * 0.0012)),
    leftY: Math.round(leftBaseY + config.leftOffsetY * (height * 0.0017) + height * variantAdjustments.leftY),
    leftWidth: Math.round(width * (presetKey === 'compact' ? 0.63 : presetKey === 'spacious' ? 0.67 : 0.65) + width * variantAdjustments.leftWidth),
    rightX: Math.round(rightBaseX + config.rightOffsetX * (width * 0.0012)),
    rightY: Math.round(rightBaseY + config.rightOffsetY * (height * 0.0017) + height * variantAdjustments.rightY),
    rightWidth: Math.round(width * (presetKey === 'compact' ? 0.28 : presetKey === 'spacious' ? 0.25 : 0.26) + width * variantAdjustments.rightWidth),
    titleFontSize: Math.round(
      height * (
        presetKey === 'compact' ? 0.175 :
        presetKey === 'spacious' ? 0.2 :
        0.176
      ) * typographyScale
    ),
    subtitleFontSize: Math.round(
      height * (
        presetKey === 'compact' ? 0.058 :
        presetKey === 'spacious' ? 0.072 :
        0.058
      ) * typographyScale
    ),
    displayNameFontSize: Math.round(
      height * (
        presetKey === 'compact' ? 0.103 :
        presetKey === 'spacious' ? 0.118 :
        0.128
      ) * typographyScale
    ),
    lineFontSize: Math.round(
      height * (
        presetKey === 'compact' ? 0.058 :
        presetKey === 'spacious' ? 0.068 :
        0.072
      ) * typographyScale
    ),
    taglineFontSize: Math.round(
      height * (
        presetKey === 'compact' ? 0.043 :
        presetKey === 'spacious' ? 0.048 :
        0.045
      ) * typographyScale
    ),
    supportTextFontSize: Math.round(
      height * (
        presetKey === 'compact' ? 0.035 :
        presetKey === 'spacious' ? 0.039 :
        0.037
      ) * typographyScale
    ),
    titleLineHeight: Math.round(
      height * (
        presetKey === 'compact' ? 0.104 :
        presetKey === 'spacious' ? 0.115 :
        0.11
      ) * typographyScale
    ),
    subtitleGap: Math.round(
      height * (
        presetKey === 'compact' ? 0.082 :
        presetKey === 'spacious' ? 0.096 :
        0.09
      ) * typographyScale
    ),
    taglineGap: Math.round(height * (presetKey === 'compact' ? 0.056 : presetKey === 'spacious' ? 0.064 : 0.06) * typographyScale),
    supportTextGap: Math.round(height * (presetKey === 'compact' ? 0.04 : presetKey === 'spacious' ? 0.046 : 0.042) * typographyScale),
    lineGap: Math.round(
      height * (
        presetKey === 'compact' ? 0.066 :
        presetKey === 'spacious' ? 0.074 :
        0.07
      ) * typographyScale
    ),
  };
};
